import { useCallback, useEffect, useState } from "react";

import StandardProjectModalUI from "./StandardProjectModal.ui.tsx";
import { StandardFormData, SelectOption } from "./StandardProjectModal.types.tsx";

import {
  getCapacities,
  getEfficiency,
  getHeatExchanger,
  getLocations,
  getRefrigerant,
  getSystem,
  getTechnology,
  getVoltages,
} from "../../services/form.services.tsx";
import { UnitSystemOptionsType } from "../../components/Settings/Settings.types.tsx";
import { ahriTemperatures } from "./ahriDefaults.tsx";

type UseStandardProjectModalProps = {
  onSubmit: (data: StandardFormData) => Promise<void>;
  title: string;
  buttonLabel: string;
  initialData?: StandardFormData;
  unitSystem: UnitSystemOptionsType;
};

const getEmptyForm = (): StandardFormData => ({
  name: "",
  description: "",
  system_id: "",
  voltage_id: "",
  technology_id: "",
  refrigerant_id: "",
  nominal_capacity: "",
  heat_exchanger_id: "",
  efficiency_id: "",
  leaving_water_temperature: "",
  entering_water_temperature: "",
  location: "",
  ambiance_temperature: "",
  amsl: "",
  running_capacity: "",
  pressure_drop: "13.8",
  fluid_type: "",
  fluid_concentration: "",
});

export const useStandardProjectModal = ({
  onSubmit,
  title,
  buttonLabel,
  initialData,
  unitSystem,
}: UseStandardProjectModalProps) => {
  const [formData, setFormData] = useState<StandardFormData>(initialData ?? getEmptyForm());

  const [validated, setValidated] = useState(false);
  const [show, setShow] = useState(false);

  const [systems, setSystems] = useState<SelectOption[]>([]);
  const [voltages, setVoltages] = useState<SelectOption[]>([]);
  const [technology, setTechnology] = useState<SelectOption[]>([]);
  const [refrigerant, setRefrigerant] = useState<SelectOption[]>([]);
  const [capacities, setCapacities] = useState<string[]>([]);
  const [heatExchanger, setHeatExchanger] = useState<SelectOption[]>([]);
  const [efficiency, setEfficiency] = useState<SelectOption[]>([]);
  const [locations, setLocations] = useState<SelectOption[]>([]);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSelectGeneric = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { name, value } = e.target;

      setFormData((prev) => {
        const updatedData = { ...prev, [name]: value };

        if (name === "location") {
          const selectedLoc = locations?.find((loc) => loc.name === value);

          if (selectedLoc) {
            return {
              ...updatedData,
              amsl: selectedLoc?.amsl?.toString(),
              ambiance_temperature: selectedLoc?.average_temperature?.toString(),
            };
          }
        }

        switch (name) {
          case "system_id":
            return {
              ...prev,
              system_id: value,
              voltage_id: "",
              technology_id: "",
              refrigerant_id: "",
              nominal_capacity: "",
              heat_exchanger_id: "",
              efficiency_id: "",
              ...ahriTemperatures(prev, value, unitSystem),
            };

          case "voltage_id":
            return {
              ...prev,
              voltage_id: value,
              technology_id: "",
              refrigerant_id: "",
              nominal_capacity: "",
              heat_exchanger_id: "",
              efficiency_id: "",
            };

          case "technology_id":
            return {
              ...prev,
              technology_id: value,
              refrigerant_id: "",
              nominal_capacity: "",
              heat_exchanger_id: "",
              efficiency_id: "",
            };

          case "refrigerant_id":
            return {
              ...prev,
              refrigerant_id: value,
              nominal_capacity: "",
              heat_exchanger_id: "",
              efficiency_id: "",
            };

          case "nominal_capacity":
            return {
              ...prev,
              nominal_capacity: value,
              heat_exchanger_id: "",
              efficiency_id: "",
            };

          case "heat_exchanger_id":
            return {
              ...prev,
              heat_exchanger_id: value,
              efficiency_id: "",
            };

          default:
            return {
              ...prev,
              [name]: value,
            };
        }
      });
    },
    [locations, unitSystem]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setValidated(true);

      if (!e.currentTarget.checkValidity()) return;

      // La capacidad en operacion solo se pide al centrifugo: en cualquier otra tecnologia el
      // campo va vacio, y `""` no deserializa a `double?`. Se manda `null` y el backend aplica
      // su valor por defecto.
      //
      // Si el calculo no salio, el formulario se queda como estaba: un centrifugo lo rechaza
      // Copeland y se acierta a mano, moviendo la capacidad en operacion hasta dar con una valida.
      try {
        await onSubmit({
          ...formData,
          running_capacity: formData.running_capacity === "" ? null : formData.running_capacity,
          // Igual con el intercambiador, que una unidad condensadora no lleva.
          heat_exchanger_id: formData.heat_exchanger_id === "" ? null : formData.heat_exchanger_id,
        });
      } catch {
        return;
      }

      setFormData(getEmptyForm());
      setShow(false);
    },
    [formData, onSubmit]
  );

  useEffect(() => {
    if (!show) return;

    getSystem().then(setSystems);
    getLocations().then(setLocations);
  }, [show]);

  useEffect(() => {
    if (!show) return;
    if (!formData.system_id) return;

    getVoltages(formData.system_id).then(setVoltages);
  }, [show, formData.system_id]);

  useEffect(() => {
    if (!show) return;
    if (!formData.voltage_id || !formData.system_id) return;

    getTechnology(formData.voltage_id, formData.system_id).then(setTechnology);
  }, [show, formData.voltage_id, formData.system_id]);

  useEffect(() => {
    if (!show) return;
    if (!formData.technology_id || !formData.system_id || !formData.voltage_id) return;

    // Con sistema y voltaje el backend solo ofrece refrigerantes con una unidad real detras; sin
    // ellos, un refrigerante sin capacidades dejaba el formulario bloqueado.
    getRefrigerant(formData.technology_id, formData.system_id, formData.voltage_id).then(
      setRefrigerant
    );
  }, [show, formData.technology_id, formData.system_id, formData.voltage_id]);

  useEffect(() => {
    if (!show) return;

    if (
      !formData.system_id ||
      !formData.voltage_id ||
      !formData.technology_id ||
      !formData.refrigerant_id
    )
      return;

    getCapacities(
      formData.system_id,
      formData.refrigerant_id,
      formData.technology_id,
      formData.voltage_id
    ).then(setCapacities);
  }, [
    show,
    formData.system_id,
    formData.voltage_id,
    formData.technology_id,
    formData.refrigerant_id,
  ]);

  useEffect(() => {
    if (!show) return;
    if (!formData.nominal_capacity || !formData.system_id || !formData.refrigerant_id) return;

    // Las unidades condensadoras no llevan intercambiador: el backend devuelve la lista vacia a
    // proposito, y entonces la eficiencia se pide sin `heat_exchanger_id`. Esperar a que se elija
    // uno dejaba el formulario bloqueado.
    getHeatExchanger(formData.system_id, formData.refrigerant_id, formData.nominal_capacity).then(
      (list) => {
        setHeatExchanger(list);
        if (list.length === 0) {
          getEfficiency(formData.nominal_capacity, formData.system_id).then(setEfficiency);
        }
      }
    );
  }, [show, formData.nominal_capacity, formData.system_id, formData.refrigerant_id]);

  useEffect(() => {
    if (!show) return;
    if (!formData.heat_exchanger_id || !formData.system_id || !formData.nominal_capacity) return;

    getEfficiency(formData.nominal_capacity, formData.system_id, formData.heat_exchanger_id).then(
      setEfficiency
    );
  }, [show, formData.heat_exchanger_id, formData.system_id, formData.nominal_capacity]);

  const open = () => {
    setValidated(false);
    setShow(true);
  };

  const close = () => setShow(false);

  const modal = show ? (
    <StandardProjectModalUI
      show={show}
      title={title}
      buttonLabel={buttonLabel}
      systems={systems}
      voltages={voltages}
      technology={technology}
      refrigerant={refrigerant}
      capacities={capacities}
      heatExchanger={heatExchanger}
      efficiency={efficiency}
      locations={locations}
      formData={formData}
      validated={validated}
      handleChange={handleChange}
      handleSelectGeneric={handleSelectGeneric}
      handleSubmit={handleSubmit}
      handleClose={close}
      unitSystem={unitSystem}
    />
  ) : null;

  return {
    open,
    close,
    modal,
  };
};
